import type { DatoPaso, PasoManual } from "./api";

/** Manual de obra (Mati, 2026-10-30): los pasos de cada subcategoría de Plata → Presupuestos. */

export const MAX_TEXTO = 300;
export const MAX_QUIEN = 80;
export const MAX_DETALLE = 1500;
export const MAX_RESPUESTA = 1000;
export const MAX_DATOS = 30;
export const MAX_NOMBRE_DATO = 120;
export const MAX_VALOR = 500;

/** El valor de un dato: lo anotado a mano manda; si no, lo que sabe el sistema. */
export const valorDato = (d: DatoPaso): string | null => d.valor || d.valor_sistema || null;

/** "3 de 5 datos" o "" sin datos. */
export function textoDatos(p: PasoManual): string {
  if (p.datos.length === 0) return "";
  const con = p.datos.filter((d) => valorDato(d)).length;
  return con === p.datos.length ? "Datos completos" : `${con} de ${p.datos.length} datos`;
}

/** Los pasos de cada subcategoría, en su orden. */
export function pasosPorSubcategoria(pasos: PasoManual[]): Map<number, PasoManual[]> {
  const salida = new Map<number, PasoManual[]>();
  for (const p of [...pasos].sort((a, b) => a.orden - b.orden || a.id - b.id)) salida.set(p.item_id, [...(salida.get(p.item_id) ?? []), p]);
  return salida;
}

/** Las subcategorías con algún paso hecho: ya se empezaron (cuentan como en trámite aunque no tengan presupuestos). */
export function subcategoriasEmpezadas(pasos: PasoManual[]): Set<number> {
  return new Set(pasos.filter((p) => p.hecho_en !== null).map((p) => p.item_id));
}

/** "3 de 8 hechos", "Todos hechos" o "" sin pasos. */
export function textoProgreso(pasos: PasoManual[]): string {
  if (pasos.length === 0) return "";
  const hechos = pasos.filter((p) => p.hecho_en !== null).length;
  return hechos === pasos.length ? "Todos hechos" : `${hechos} de ${pasos.length} hechos`;
}

/** Los pasos con dato que falta anotar. */
export const faltaDato = (p: PasoManual): boolean => p.pide_dato && !p.respuesta;

/** Quién suele saber o hacer cada paso (el campo deja escribir otro). */
export const QUIENES = [
  "Admin", "Arquitecto", "Calculista", "Director de obra", "Jefe de obra", "Proveedor", "Escribano", "Contador", "Agrimensor",
  "Higiene y seguridad", "Municipio", "Empresa de servicios", "Productor de seguros",
];

export interface FormDato {
  nombre: string;
  quien: string;
  variable: string | null;
  /** Lo que sabe el sistema (sólo para mostrar). */
  valor_sistema: string | null;
  valor: string;
}

export interface FormPaso {
  texto: string;
  quien: string;
  detalle: string;
  pide_dato: boolean;
  respuesta: string;
  datos: FormDato[];
}

export type ErroresPaso = Partial<Record<Exclude<keyof FormPaso, "datos">, string>> & { datos?: string };

export const datoVacio = (): FormDato => ({ nombre: "", quien: "", variable: null, valor_sistema: null, valor: "" });

export function formPaso(p?: PasoManual): FormPaso {
  return {
    texto: p?.texto ?? "", quien: p?.quien ?? "", detalle: p?.detalle ?? "", pide_dato: p?.pide_dato ?? false, respuesta: p?.respuesta ?? "",
    datos: (p?.datos ?? []).map((d) => ({ nombre: d.nombre, quien: d.quien ?? "", variable: d.variable, valor_sistema: d.valor_sistema, valor: d.valor ?? "" })),
  };
}

/** Las mismas reglas que el backend (app/manual/reglas.py). */
export function validarPaso(f: FormPaso): ErroresPaso {
  const e: ErroresPaso = {};
  const texto = f.texto.trim();
  if (!texto) e.texto = "Escribí el paso";
  else if (texto.length > MAX_TEXTO) e.texto = `Hasta ${MAX_TEXTO} caracteres`;
  if (f.quien.trim().length > MAX_QUIEN) e.quien = `Hasta ${MAX_QUIEN} caracteres`;
  if (f.detalle.trim().length > MAX_DETALLE) e.detalle = `Hasta ${MAX_DETALLE} caracteres`;
  if (f.respuesta.trim().length > MAX_RESPUESTA) e.respuesta = `Hasta ${MAX_RESPUESTA} caracteres`;
  if (f.datos.length > MAX_DATOS) e.datos = `Hasta ${MAX_DATOS} datos`;
  else if (f.datos.some((d) => !d.nombre.trim())) e.datos = "Cada dato necesita un nombre";
  else if (f.datos.some((d) => d.nombre.trim().length > MAX_NOMBRE_DATO)) e.datos = `El nombre de un dato: hasta ${MAX_NOMBRE_DATO} caracteres`;
  else if (f.datos.some((d) => d.valor.trim().length > MAX_VALOR)) e.datos = `El valor de un dato: hasta ${MAX_VALOR} caracteres`;
  return e;
}

export function aPayloadPaso(f: FormPaso) {
  const o = (t: string) => t.trim() || null;
  return {
    texto: f.texto.trim(), quien: o(f.quien), detalle: o(f.detalle), pide_dato: f.pide_dato, respuesta: f.pide_dato ? o(f.respuesta) : null,
    datos: f.datos.map((d) => ({ nombre: d.nombre.trim(), quien: o(d.quien), variable: d.variable, valor: o(d.valor) })),
  };
}

/** "Se copiaron 3 categorías, 40 subcategorías y 200 pasos." */
export function textoCopia(r: { categorias: number; subcategorias: number; pasos: number }): string {
  if (r.categorias + r.subcategorias + r.pasos === 0) return "Ya estaba todo: no se copió nada.";
  const n = (k: number, uno: string, varios: string) => `${k} ${k === 1 ? uno : varios}`;
  return `Se copiaron ${n(r.categorias, "categoría", "categorías")}, ${n(r.subcategorias, "subcategoría", "subcategorías")} y ${n(r.pasos, "paso", "pasos")}.`;
}

/** "Se cargaron 412 pasos en 150 subcategorías." */
export function textoSugeridos(r: { subcategorias: number; pasos: number }): string {
  if (r.subcategorias === 0) return "Todas las subcategorías ya tenían pasos: no se cargó nada.";
  return `Se cargaron ${r.pasos} pasos en ${r.subcategorias === 1 ? "1 subcategoría" : `${r.subcategorias} subcategorías`}. Revisalos y corregí lo que no aplique.`;
}
